import { useCallback, useEffect, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useRecoilState } from 'recoil';
import dayjs, { Dayjs } from 'dayjs';

import {
  activityEventsAtom,
  selectedActivityEventAtom,
  useAuthContext,
  useCalendarContext,
  useDeleteNapDetails,
  useEventActions,
  useHideExercise,
  useHideMeal,
  useMetricsContext,
} from '../data-access';
import {
  buildNapSummaryRichContextFromActivityEvent,
  buildWorkoutSummaryRichContextFromActivityEvent,
  translateActivityToTemporalCalendarEvent,
  translateNapToTemporalCalendarEvent,
} from '../helpers';
import { getCssVariable } from '../utils';

const PRIMARY_COLOR = getCssVariable('--color-primary');

export const useMetricsActivitiesInCalendar = () => {
  const { user } = useAuthContext();
  const { exerciseDetails, mealDetails, sleepDetails } = useMetricsContext();
  const { createCalendarEvent } = useEventActions();
  const { mainCalendar, calendarPreferences } = useCalendarContext();
  const { hideExericse } = useHideExercise();
  const { hideMeal } = useHideMeal();
  const { deleteNapDetail } = useDeleteNapDetails();

  const [activityEvents, setActivityEvents] = useRecoilState(activityEventsAtom);
  const [selectedActivityEvent, setSelectedActivityEvent] =
    useRecoilState(selectedActivityEventAtom);

  useEffect(() => {
    const exercises = Array.isArray(exerciseDetails)
      ? exerciseDetails
          .map(({ details }) => details)
          .flat()
          .filter((e) => !e.hide)
      : [];

    // Merge meals with the same start time

    const exerciseEvents = calendarPreferences?.hideAutoSchedule?.exercise === true
      ? []
      : exercises.map((activity) => translateActivityToTemporalCalendarEvent(activity, 'exercise'));

    const napEvents = Array.isArray(sleepDetails)
      ? sleepDetails
          .flatMap(({ nap = [] }) => nap)
          .map((napItem) => translateNapToTemporalCalendarEvent(napItem))
          .filter(Boolean)
      : [];

    setActivityEvents([...exerciseEvents, ...napEvents]);
  }, [exerciseDetails, mealDetails, sleepDetails, calendarPreferences?.hideAutoSchedule?.exercise]);

  const clearSelectedActivityEvent = () => {
    setSelectedActivityEvent(null);
  };

  const handleSaveActivityEvent = useCallback(
    (calendarId?: string, startTime?: Dayjs, endTime?: Dayjs) => {
      if (!selectedActivityEvent) {
        clearSelectedActivityEvent();
        return;
      }

      const isExercise = selectedActivityEvent.activityType === 'exercise';
      const isNap = selectedActivityEvent.activityType === 'nap';
      const updatedStart = startTime ? startTime.toISOString() : selectedActivityEvent.start.date;
      const updatedEnd = endTime ? endTime.toISOString() : selectedActivityEvent.end.date;

      const richContext = isExercise
        ? buildWorkoutSummaryRichContextFromActivityEvent(selectedActivityEvent, {
            startTime: updatedStart,
            endTime: updatedEnd,
          })
        : isNap
          ? buildNapSummaryRichContextFromActivityEvent(selectedActivityEvent, {
              startTime: updatedStart,
              endTime: updatedEnd,
            })
          : undefined;

      createCalendarEvent({
        calendarId,
        newEventOption: {
          ...selectedActivityEvent,
          eventId: (uuidv4() as string).replace(/-/g, ''),
          color: mainCalendar?.color || PRIMARY_COLOR,
          scheduleFrom: isExercise ? 'workout' : isNap ? 'sleep' : 'activity',
          ...(isExercise
            ? {
                autoScheduleFrom: {
                  from: 'workout' as const,
                  id: selectedActivityEvent.activityFrom?.activityId ?? '',
                },
                richContext,
              }
            : isNap
              ? {
                  autoScheduleFrom: {
                    from: 'sleep' as const,
                    id: selectedActivityEvent.activityFrom?.activityId ?? '',
                    chatSessionId: 'nap',
                  },
                  richContext,
                }
              : {}),
          start: {
            ...selectedActivityEvent.start,
            date: updatedStart,
          },
          end: {
            ...selectedActivityEvent.end,
            date: updatedEnd,
          },
        },
        metrics: { energy: 'high' },
      });
      setActivityEvents((prev) => prev.filter((e) => e.eventId !== selectedActivityEvent.eventId));
      clearSelectedActivityEvent();
    },
    [user.userId, mainCalendar, activityEvents, selectedActivityEvent]
  );

  const handleDiscardActivityEvent = useCallback(() => {
    if (!selectedActivityEvent || !selectedActivityEvent.activityFrom?.activityId) {
      clearSelectedActivityEvent();

      return;
    }
    if (selectedActivityEvent.activityType === 'exercise') {
      hideExericse({
        metricId: user.metricId!,
        date: dayjs(selectedActivityEvent.start.date).format('YYYY-MM-DD'),
        activityId: selectedActivityEvent.activityFrom?.activityId,
      });
    } else if (selectedActivityEvent.activityType === 'nap') {
      deleteNapDetail({
        userId: user.userId,
        metricId: user.metricId!,
        date: dayjs(selectedActivityEvent.end.date).format('YYYY-MM-DD'),
        metricNapId: selectedActivityEvent.activityFrom.activityId,
      }).catch(() => undefined);
    } else if (selectedActivityEvent.activityType === 'meal') {
      hideMeal({
        metricId: user.metricId!,
        date: dayjs(selectedActivityEvent.start.date).format('YYYY-MM-DD'),
        mealId: selectedActivityEvent.activityFrom?.activityId,
      });
    }

    setActivityEvents((prev) => prev.filter((e) => e.eventId !== selectedActivityEvent.eventId));
    clearSelectedActivityEvent();
  }, [deleteNapDetail, hideExericse, hideMeal, selectedActivityEvent, user.metricId, user.userId]);

  const handleSelectActivityEvent = useCallback(
    (eventId: string) => {
      const targetEvent = activityEvents.find((e) => e.eventId === eventId);
      setSelectedActivityEvent(targetEvent);
    },
    [activityEvents]
  );

  return {
    activityEvents,
    selectedActivityEvent,
    clearSelectedActivityEvent,
    handleSaveActivityEvent,
    handleDiscardActivityEvent,
    handleSelectActivityEvent,
  };
};
